(() => {
  const FIT_KEY = 'faf:fit-preferences';
  const ACCOUNT_NODE_ID = 'faf-customer-fit';
  const CANONICAL_PROFILE_KEYS = new Set([
    'womensFootwearSize',
    'mensFootwearSize',
    'womensApparelNumeric',
    'womensApparelAlpha',
    'womensBottomsNumeric',
    'mensApparelAlpha',
    'calfCircumference',
    'preferredSizeSystem'
  ]);
  const ALPHA_SIZE_RE = /^(XXS|XS|S|M|L|XL|XXL|XXXL)$/i;
  const CALF_FIT_ORDER = ['Narrow Fit', 'Regular Fit', 'Sporting Fit'];
  const isPresent = (value) => value !== null && value !== undefined && String(value).trim() !== '';

  const readLocalFit = () => {
    try {
      const value = JSON.parse(localStorage.getItem(FIT_KEY) || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (_) { return {}; }
  };
  const readAccountFit = () => {
    const node = document.getElementById(ACCOUNT_NODE_ID);
    if (!node) return null;
    try {
      const value = JSON.parse(node.textContent || '{}');
      return value && typeof value === 'object' ? value : {};
    } catch (_) { return {}; }
  };
  const normalizeGender = (value) => {
    const gender = String(value || '').trim().toLowerCase();
    if (gender.includes('women')) return 'womens';
    if (gender.includes('men')) return 'mens';
    return '';
  };
  const sizeScale = (rawValues = []) => {
    let alpha = false;
    let numeric = false;
    rawValues.forEach((raw) => {
      const value = String(raw || '').trim();
      if (!value) return;
      const withoutSystem = value.replace(/^(UK|EU|US)\s+/i, '').trim();
      if (ALPHA_SIZE_RE.test(withoutSystem)) alpha = true;
      if (/\d/.test(value)) numeric = true;
    });
    if (alpha && !numeric) return 'alpha';
    if (numeric && !alpha) return 'numeric';
    return '';
  };
  const resolveProfileKey = (contextKey, {gender = '', rawValues = []} = {}) => {
    const key = String(contextKey || '').trim();
    if (CANONICAL_PROFILE_KEYS.has(key)) return key;
    const resolvedGender = normalizeGender(gender);
    const scale = sizeScale(rawValues);
    if (key === 'footwearSize') {
      if (resolvedGender === 'womens') return 'womensFootwearSize';
      if (resolvedGender === 'mens') return 'mensFootwearSize';
      return '';
    }
    if (key === 'waistSize') return resolvedGender === 'womens' && scale !== 'alpha' ? 'womensBottomsNumeric' : '';
    if (key === 'dressSize') {
      if (resolvedGender === 'womens' && scale === 'numeric') return 'womensApparelNumeric';
      if (resolvedGender === 'womens' && scale === 'alpha') return 'womensApparelAlpha';
      if (resolvedGender === 'mens' && scale === 'alpha') return 'mensApparelAlpha';
    }
    return '';
  };
  const migrateLegacyFit = (value) => {
    const next = {...(value || {})};
    if (!isPresent(next.preferredSizeSystem) && isPresent(next.sizeSystem)) next.preferredSizeSystem = next.sizeSystem;
    if (isPresent(next.preferredSizeSystem)) next.sizeSystem = String(next.preferredSizeSystem).toUpperCase();
    const footwearGender = normalizeGender(next.footwearGender);
    const footwearKey = footwearGender === 'womens' ? 'womensFootwearSize' : footwearGender === 'mens' ? 'mensFootwearSize' : '';
    if (footwearKey && isPresent(next.footwearSize) && !isPresent(next[footwearKey])) next[footwearKey] = next.footwearSize;
    if (footwearKey && isPresent(next.footwearSizeRaw) && !isPresent(next[`${footwearKey}Raw`])) next[`${footwearKey}Raw`] = next.footwearSizeRaw;
    const dressValue = String(next.dressSize || '').trim();
    const dressRaw = String(next.dressSizeRaw || '').trim();
    if (dressValue) {
      const dressKey = ALPHA_SIZE_RE.test(dressValue) ? 'womensApparelAlpha' : 'womensApparelNumeric';
      if (!isPresent(next[dressKey])) next[dressKey] = dressValue;
      if (dressRaw && !isPresent(next[`${dressKey}Raw`])) next[`${dressKey}Raw`] = dressRaw;
    }
    if (isPresent(next.waistSize) && !isPresent(next.womensBottomsNumeric)) next.womensBottomsNumeric = next.waistSize;
    if (isPresent(next.waistSizeRaw) && !isPresent(next.womensBottomsNumericRaw)) next.womensBottomsNumericRaw = next.waistSizeRaw;
    return next;
  };
  const cleanCanonicalProfile = (value = {}) => {
    const next = {};
    CANONICAL_PROFILE_KEYS.forEach((key) => {
      const raw = value[key];
      next[key] = isPresent(raw) ? String(raw).trim() : '';
    });
    return next;
  };
  const canonicalUkSize = (value) => {
    const text = String(value || '').trim();
    const match = text.match(/^UK\s+([0-9]+(?:\.[0-9]+)?)$/i) || text.match(/^([0-9]+(?:\.[0-9]+)?)$/);
    if (!match) return '';
    const number = Number(match[1]);
    return Number.isFinite(number) ? String(number) : '';
  };
  const guideUkSize = (value) => {
    const text = String(value || '').trim();
    const explicit = text.match(/\bUK\s*([0-9]+(?:\.[0-9]+)?)/i);
    if (explicit) return String(Number(explicit[1]));
    const parts = text.split('/').map((part) => part.trim());
    if (parts.length >= 3 && /^[0-9]+(?:\.[0-9]+)?$/.test(parts[1])) return String(Number(parts[1]));
    return '';
  };
  const parseCalfRange = (value) => {
    const match = String(value || '').match(/([0-9]+(?:\.[0-9]+)?)\s*[-–—]\s*([0-9]+(?:\.[0-9]+)?)/);
    if (!match) return null;
    const min = Number(match[1]);
    const max = Number(match[2]);
    if (!Number.isFinite(min) || !Number.isFinite(max) || min > max) return null;
    return {min, max};
  };
  const parseSizeGuide = (value) => {
    if (value && typeof value === 'object') return value;
    try {
      const parsed = JSON.parse(value || '{}');
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) { return {}; }
  };
  const resolveCalfFit = ({profile = readFit(), sizeGuide = window.currentProduct?.sizeGuide, availableFits = []} = {}) => {
    const size = canonicalUkSize(profile?.womensFootwearSize);
    if (!size) return {status: 'missing-size'};
    const measurement = Number(profile?.calfCircumference);
    if (!Number.isFinite(measurement) || measurement <= 0) return {status: 'missing-calf', size};
    const guide = parseSizeGuide(sizeGuide);
    const sizes = Array.isArray(guide.size) ? guide.size : [];
    const calfFits = guide.calf_fits && typeof guide.calf_fits === 'object' ? guide.calf_fits : {};
    if (!sizes.length || !Object.keys(calfFits).length) return {status: 'no-guide', size, measurement};
    const sizeIndex = sizes.findIndex((guideSize) => guideUkSize(guideSize) === size);
    if (sizeIndex < 0) return {status: 'size-not-found', size, measurement};
    const allowedFits = new Set((Array.isArray(availableFits) ? availableFits : []).map((fit) => String(fit || '').trim()).filter(Boolean));
    const restrictFits = allowedFits.size > 0;
    const matches = [];
    CALF_FIT_ORDER.forEach((fitName) => {
      if (restrictFits && !allowedFits.has(fitName)) return;
      const fit = calfFits[fitName];
      const ranges = Array.isArray(fit) ? fit : fit?.cm;
      const rangeValue = Array.isArray(ranges) ? ranges[sizeIndex] : '';
      const range = parseCalfRange(rangeValue);
      if (!range) return;
      if (measurement >= range.min && measurement <= range.max) matches.push({fit: fitName, range: String(rangeValue).trim(), min: range.min, max: range.max});
    });
    if (!matches.length) return {status: 'no-match', size, sizeIndex, measurement, matches: []};
    const selected = matches[0];
    return {status: 'matched', fit: selected.fit, size, sizeIndex, measurement, range: selected.range, matches: matches.map((match) => match.fit)};
  };
  const accountIsConfirmed = (account) => Boolean(account && isPresent(account.profileLastConfirmedAt));
  const readFit = () => {
    const local = migrateLegacyFit(readLocalFit());
    const account = readAccountFit();
    if (!account) return local;
    const confirmed = accountIsConfirmed(account);
    const next = {...local};
    if (confirmed) {
      CANONICAL_PROFILE_KEYS.forEach((key) => { delete next[key]; delete next[`${key}Raw`]; });
    }
    Object.entries(account).forEach(([key, value]) => {
      if (confirmed && CANONICAL_PROFILE_KEYS.has(key)) {
        next[key] = isPresent(value) ? String(value).trim() : '';
        return;
      }
      if (isPresent(value)) next[key] = value;
    });
    if (isPresent(account.preferredSizeSystem)) next.sizeSystem = String(account.preferredSizeSystem).toUpperCase();
    const resolved = migrateLegacyFit(next);
    delete resolved.calfFit;
    return resolved;
  };
  const saveFit = (value) => {
    const next = {...value, version: 2};
    try { localStorage.setItem(FIT_KEY, JSON.stringify(next)); } catch (_) {}
    const resolved = readFit();
    dispatchEvent(new CustomEvent('faf:fit-profile-changed', {detail: resolved}));
    return resolved;
  };
  const saveLocalProfile = (profile) => {
    const next = {...cleanCanonicalProfile(profile), version: 3};
    try { localStorage.setItem(FIT_KEY, JSON.stringify(next)); } catch (_) {}
    const resolved = readFit();
    dispatchEvent(new CustomEvent('faf:fit-profile-changed', {detail: resolved}));
    return resolved;
  };
  const applyAccountProfile = (profile, confirmation = {}) => {
    const node = document.getElementById(ACCOUNT_NODE_ID);
    if (!node) return readFit();
    const current = readAccountFit() || {};
    const next = {...current, ...cleanCanonicalProfile(profile)};
    if (isPresent(confirmation.confirmedAt)) next.profileLastConfirmedAt = String(confirmation.confirmedAt).trim();
    if (isPresent(confirmation.source)) next.profileLastConfirmedSource = String(confirmation.source).trim();
    node.textContent = JSON.stringify(next);
    const resolved = readFit();
    dispatchEvent(new CustomEvent('faf:fit-profile-changed', {detail: resolved}));
    return resolved;
  };
  const persistFitProfile = async (profile) => {
    const canonical = cleanCanonicalProfile(profile);
    if (readAccountFit() === null) return saveLocalProfile(canonical);
    const response = await fetch('/apps/fairfax-customer', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({profile: canonical, confirmProfile: true, source: 'store'})});
    const text = await response.text();
    let payload = {};
    if (text) { try { payload = JSON.parse(text); } catch (_) {} }
    if (!response.ok || !payload.ok) throw new Error(payload.error || `Unable to save fit preferences (${response.status}).`);
    const confirmedAt = String(payload.confirmedAt || '').trim();
    if (payload.confirmed !== true || !confirmedAt) throw new Error('Fit profile was saved, but no confirmation timestamp was returned.');
    return applyAccountProfile({...canonical, ...(payload.profile || {})}, {confirmedAt, source: payload.source || 'store'});
  };
  const reconcileLocalProfile = async () => {
    const account = readAccountFit();
    if (account === null || accountIsConfirmed(account)) return readFit();
    const local = migrateLegacyFit(readLocalFit());
    const promotion = {};
    CANONICAL_PROFILE_KEYS.forEach((key) => {
      if (!isPresent(account[key]) && isPresent(local[key])) promotion[key] = String(local[key]).trim();
    });
    if (!Object.keys(promotion).length) return readFit();
    try {
      const response = await fetch('/apps/fairfax-customer', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({profile: promotion, confirmProfile: false, source: 'store'})});
      const result = await response.json().catch(() => null);
      if (!response.ok || !result?.ok) throw new Error(result?.error || `Unable to reconcile fit preferences (${response.status}).`);
      const accountNode = document.getElementById(ACCOUNT_NODE_ID);
      if (accountNode) accountNode.textContent = JSON.stringify({...readAccountFit(), ...(result.profile || promotion)});
      const resolved = readFit();
      window.dispatchEvent(new CustomEvent('faf:fit-profile-changed', {detail: resolved}));
      return resolved;
    } catch (error) {
      console.error('Unable to reconcile guest fit preferences.', error);
      return readFit();
    }
  };
  window.FAFFitProfile = {
    read: readFit, readLocal: readLocalFit, readAccount: readAccountFit,
    saveProfile: persistFitProfile, saveLocalProfile, applyAccountProfile,
    resolveProfileKey, resolveCalfFit,
    isAccountConfirmed: () => accountIsConfirmed(readAccountFit()),
    isAccountBacked: () => readAccountFit() !== null,
  };
  reconcileLocalProfile();

  const norm = (value) => String(value || '').trim().toUpperCase().replace(/\s+/g, ' ').replace(/\s*\/\s*/g, ' / ');
  const aliases = (raw, node) => {
    const values = new Set([norm(raw)]);
    if (!window.FAFSizes) return values;
    ['UK', 'EU', 'US'].forEach((preference) => values.add(norm(window.FAFSizes.format(raw, {gender: node?.dataset?.sizeGender || '', category: node?.dataset?.sizeCategory || '', preference}))));
    return values;
  };
const wantedAliases = (saved, key) => {
  const value = String(saved[key] || '').trim();
  const raw = String(saved[`${key}Raw`] || '').trim();

  const values = new Set(
    [norm(value), norm(raw)].filter(Boolean)
  );

  /*
   * Numeric fit-profile sizes are stored canonically as UK sizes.
   * The customer's UK / EU / US preference controls presentation only;
   * it must not affect which Shopify variant is considered their size.
   */
  if (/^[0-9.]+$/.test(value)) {
    values.add(norm(`UK ${value}`));
  }

  return values;
};

  function register() {
    if (!window.Alpine || window.FAFOrchestrationRegistered) return;
    window.FAFOrchestrationRegistered = true;

    Alpine.data('fitProfileEditor', () => ({
      saved: {}, status: '',
      init() { this.saved = readFit(); this.$nextTick(() => Object.entries(this.saved).forEach(([name, value]) => { const field = this.$root.elements?.namedItem(name); if (field && value != null) field.value = value; })); },
      save() {
        const form = Object.fromEntries(new FormData(this.$root));
        const next = {...this.saved, ...form};
        ['footwearSize', 'dressSize', 'waistSize'].forEach((key) => { if (String(form[key] || '').trim() !== String(this.saved[key] || '').trim()) delete next[`${key}Raw`]; });
        this.saved = saveFit(next);
        if (form.sizeSystem && Alpine.store('sizeSystem')?.set) Alpine.store('sizeSystem').set(form.sizeSystem);
        this.status = 'Fit profile saved.';
      }
    }));

    Alpine.data('pdpFitRecommendation', (config = {}) => ({
      saved: {}, message: '', preview: '', selectedVariantId: '', calfRecommendation: null,
      productId: String(config.productId || ''), profileContextKey: String(config.profileKey || ''), profileLabel: String(config.profileLabel || 'size'), profileGender: String(config.sizeGender || ''), fitDirection: Number(config.fitDirection || 0), fitReason: String(config.fitReason || ''), currentCalfFit: String(config.currentCalfFit || ''),
      resolvedProfileKey() {
        const entries = this.entries();
        const entryGender = String(entries.find((entry) => entry.node?.dataset?.sizeGender)?.node?.dataset?.sizeGender || '');
        return resolveProfileKey(this.profileContextKey, {gender: entryGender || this.profileGender, rawValues: entries.map((entry) => entry.raw)});
      },
      get profileKey() { return this.resolvedProfileKey(); },
      get rawKey() { return `${this.profileKey}Raw`; },
      get profileValue() { return String(this.saved[this.profileKey] || ''); },
      init() { this.saved = readFit(); this.$nextTick(() => { this.selectedVariantId = String(this.entries().find((entry) => entry.input.checked)?.input?.value || ''); this.refreshPreview(); this.refreshCalfRecommendation(); }); },
      onProfileChanged(event) { this.saved = event.detail || readFit(); this.refreshPreview(); this.refreshCalfRecommendation(); },
      entries() { return [...document.querySelectorAll(`[data-variant-selector] input[data-pdp-product-id='${this.productId}']`)].map((input) => { const label = input.id ? document.querySelector(`label[for='${input.id}']`) : null; const node = label?.querySelector('[data-raw-size]'); const raw = String(node?.dataset.rawSize || '').trim(); return node && raw ? {input, label, node, raw} : null; }).filter(Boolean); },
      selectedEntry() { return this.entries().find((entry) => String(entry.input.value) === String(this.selectedVariantId) || entry.input.checked) || null; },
      selectionIsSaved() { if (!this.profileKey || !this.profileValue) return false; const selected = this.selectedEntry(); const savedEntry = this.baseline(this.entries()); return Boolean(selected && savedEntry && selected.input === savedEntry.input); },
      purchasable(entry) { const value = String(entry?.input?.dataset?.purchasable || '').toLowerCase(); return value === 'true' || (value !== 'false' && !entry?.input?.disabled); },
      canonical(entry) { return norm(window.FAFSizes?.format(entry.raw, {gender: entry.node.dataset.sizeGender || '', category: entry.node.dataset.sizeCategory || '', preference: 'UK'}) || entry.raw); },
      rank(entry) { const key = this.canonical(entry); const number = key.match(/\bUK\s*([0-9]+(?:\.[0-9]+)?)/i) || key.match(/^([0-9]+(?:\.[0-9]+)?)$/); if (number) return Number(number[1]); return ({XXS:0, XS:1, S:2, M:3, L:4, XL:5, XXL:6, XXXL:7})[key.replace(/^(UK|EU|US)\s+/i, '')] ?? null; },
      baseline(entries) { const wanted = wantedAliases(this.saved, this.profileKey); return entries.find((entry) => [...wanted].some((value) => aliases(entry.raw, entry.node).has(value))) || null; },
      result() {
        if (!this.profileKey || !this.profileValue) return {status: 'no-profile'};
        const entries = this.entries(); const baseline = this.baseline(entries);
        if (!baseline) return {status: 'baseline-not-offered'};
        if (!this.fitDirection) return {status: this.purchasable(baseline) ? 'ready' : 'unavailable', baseline, target: baseline, adjusted: false};
        const groups = new Map(); entries.forEach((entry) => { const key = this.canonical(entry); if (!groups.has(key)) groups.set(key, {key, rank: this.rank(entry), entries: []}); groups.get(key).entries.push(entry); });
        const ladder = [...groups.values()].sort((a,b) => (a.rank ?? 999) - (b.rank ?? 999));
        const index = ladder.findIndex((group) => group.key === this.canonical(baseline));
        const group = ladder[index + (this.fitDirection > 0 ? 1 : -1)];
        if (!group) return {status: 'no-adjacent', baseline};
        const target = group.entries.find((entry) => this.purchasable(entry)) || group.entries[0];
        return {status: this.purchasable(target) ? 'ready' : 'unavailable', baseline, target, adjusted: true};
      },
      label(entry) { return entry ? window.FAFSizes?.format(entry.raw, {gender: entry.node.dataset.sizeGender || '', category: entry.node.dataset.sizeCategory || '', preference: window.FAFSizes.preference || this.saved.sizeSystem || 'UK'}) || entry.raw : ''; },
      refreshPreview() { const result = this.result(); if (!result.target) return void (this.preview = ''); const direction = this.fitDirection > 0 ? 'one size up' : 'one size down'; this.preview = `Recommended for you: ${this.label(result.target)}${result.adjusted ? ` · ${direction}` : ''}`; },
      calfAvailableFits() { return [...this.$root.querySelectorAll('[data-calf-fit-option]')].map((node) => String(node.dataset.fitName || '').trim()).filter(Boolean); },
      refreshCalfRecommendation() { const availableFits = this.calfAvailableFits(); if (!availableFits.length) { this.calfRecommendation = null; return; } this.calfRecommendation = resolveCalfFit({profile: this.saved, sizeGuide: window.currentProduct?.sizeGuide, availableFits}); },
      isRecommendedCalfFit(fitName) { return this.calfRecommendation?.status === 'matched' && this.calfRecommendation.fit === String(fitName || '').trim(); },
      calfRecommendationText() {
        const result = this.calfRecommendation; if (!result) return '';
        if (result.status === 'matched') {
          const fit = String(result.fit || '').replace(/\s+Fit$/i, '');
          const preference = String(window.FAFSizes?.preference || this.saved.preferredSizeSystem || this.saved.sizeSystem || 'UK').toUpperCase();
          const displayedSize = window.FAFSizes?.format?.('UK ' + result.size, {gender: 'womens', category: 'footwear', preference}) || `UK ${result.size}`;
          const cm = Number(this.saved.calfCircumference || result.measurement || 0);
          const measurement = Number.isFinite(cm) && cm > 0 ? preference === 'US' ? `${Math.round((cm / 2.54) * 10) / 10} in` : `${Math.round(cm * 10) / 10} cm` : '';
          return 'Your calf fit: ' + fit + ' · ' + displayedSize + (measurement ? ' · ' + measurement : '');
        }
        if (result.status === 'missing-size') return 'Add your usual shoe size for a personalised fit.';
        if (result.status === 'missing-calf') return 'Add your calf measurement for a personalised fit.';
        return String(result.message || '').trim();
      },
      async openFitPreferences(field = 'calf') {
        const panel = Alpine.store('lazyPanel');
        if (!panel?.openAccountView) return;
        const opened = await panel.openAccountView('overview'); if (!opened) return;
        window.setTimeout(() => { window.dispatchEvent(new CustomEvent('faf:open-fit-preferences', {detail: {field}})); }, 80);
      },
      onVariant(event) { if (String(event.detail?.productId || '') === this.productId) this.selectedVariantId = String(event.detail?.variantId || ''); this.message = ''; },
      async saveSelection() {
        const entry = this.selectedEntry();
        if (!entry) { this.message = `Choose a ${this.profileLabel.toLowerCase()} first.`; return; }
        if (!this.profileKey) { this.message = 'This size cannot be saved to your fit profile.'; return; }
        const sizeSystem = window.FAFSizes?.preference || this.saved.sizeSystem || 'UK';
        const displayValue = window.FAFSizes?.format(entry.raw, {gender: entry.node.dataset.sizeGender || '', category: entry.node.dataset.sizeCategory || '', preference: sizeSystem}) || entry.raw;
        const canonicalValue = String(this.canonical(entry)).replace(/^UK\s+/i, '').trim();
        if (!canonicalValue) { this.message = 'Unable to resolve this size.'; return; }
        const account = readAccountFit();
        if (account === null) {
          this.saved = saveFit({...this.saved, [this.profileKey]: canonicalValue, [this.rawKey]: entry.raw, sizeSystem});
          this.message = window.FAFFitProfile?.isAccountBacked?.() ? `Saved ${displayValue} to your account.` : `Saved ${displayValue} on this device.`;
          this.refreshPreview(); return;
        }
        this.message = 'Saving…';
        try {
          const response = await fetch('/apps/fairfax-customer', {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({profile: {[this.profileKey]: canonicalValue}, confirmProfile: false, source: 'pdp'})});
          const result = await response.json().catch(() => null);
          if (!response.ok || !result?.ok) throw new Error(result?.error || `Unable to save size (${response.status}).`);
          const accountNode = document.getElementById(ACCOUNT_NODE_ID);
          if (accountNode) accountNode.textContent = JSON.stringify({...readAccountFit(), ...(result.profile || {[this.profileKey]: canonicalValue})});
          this.saved = {...readFit(), [this.rawKey]: entry.raw, sizeSystem};
          window.dispatchEvent(new CustomEvent('faf:fit-profile-changed', {detail: this.saved}));
          this.message = window.FAFFitProfile?.isAccountBacked?.() ? `Saved ${displayValue} to your account.` : `Saved ${displayValue} on this device.`;
          this.refreshPreview();
        } catch (error) { console.error('Unable to save PDP fit preference.', error); this.message = error?.message || 'Unable to save this size.'; }
      },
      chooseRecommendation() {
        const result = this.result();
        if (result.status === 'no-profile') return void (this.message = `Save your usual ${this.profileLabel.toLowerCase()} first.`);
        if (result.status === 'baseline-not-offered') return void (this.message = `Your saved ${this.profileLabel.toLowerCase()} is not offered for this product.`);
        if (result.status === 'no-adjacent') return void (this.message = 'No reliable adjacent size is available.');
        const target = this.label(result.target);
        if (result.status === 'unavailable') return void (this.message = `${target} is currently unavailable.`);
        result.target.input.click(); this.selectedVariantId = String(result.target.input.value || ''); this.message = `Selected ${target}.`;
      }
    }));

    
    Alpine.store('fitProfile', {
      profile: readFit(),
      revision: 0,

      replace(profile = {}) {
        this.profile =
          profile && typeof profile === 'object'
            ? { ...profile }
            : {};

        this.revision += 1;
      },

      refresh() {
        this.replace(readFit());
      },

      get(key) {
        return this.profile?.[key] ?? '';
      },
    });

    if (!window.__fafFitProfileStoreBridgeBound) {
      window.__fafFitProfileStoreBridgeBound = true;

      window.addEventListener(
        'faf:fit-profile-changed',
        (event) => {
          const store =
            window.Alpine?.store?.('fitProfile');

          if (!store) return;

          const profile =
            event.detail &&
            typeof event.detail === 'object'
              ? event.detail
              : readFit();

          store.replace(profile);
        }
      );
    }
        

Alpine.data('savedSizeStatus', (config = {}) => ({
  profileContextKey: String(config.profileKey || ''),
  available: false,
  label: '',
  retryTimer: null,
  retryCount: 0,

  init() {
    this.refresh();
  },

  destroy() {
    window.clearTimeout(this.retryTimer);
  },

  queueRetry() {
    window.clearTimeout(this.retryTimer);

    if (this.retryCount >= 8) return;

    this.retryCount += 1;

    this.retryTimer = window.setTimeout(
      () => this.refresh(),
      50 * this.retryCount
    );
  },

  sync() {
    /*
     * These reads make the Alpine effect reactive to:
     * 1. saved-fit/profile changes;
     * 2. UK/EU/US display preference changes.
     */
    void this.$store.fitProfile?.revision;
    void this.$store.sizeSystem?.preference;

    this.refresh();
  },

  refresh() {
    this.available = false;
    this.label = '';

    if (
      !window.FAFSizes ||
      !window.FAFFitProfile?.read
    ) {
      this.queueRetry();
      return;
    }

    this.retryCount = 0;

    const nodes = [
      ...this.$root.querySelectorAll(
        '[data-saved-size-raw]'
      ),
    ];

    if (!nodes.length) return;

    const gender = String(
      nodes.find(
        (node) => node.dataset.sizeGender
      )?.dataset.sizeGender || ''
    );

    const profileKey = resolveProfileKey(
      this.profileContextKey,
      {
        gender,
        rawValues: nodes.map(
          (node) =>
            node.dataset.savedSizeRaw || ''
        ),
      }
    );

    if (!profileKey) return;

    const saved =
      this.$store.fitProfile?.profile ||
      readFit();

    const wanted = wantedAliases(
      saved,
      profileKey
    );

    const match = nodes.find((node) =>
      [...wanted].some((value) =>
        aliases(
          node.dataset.savedSizeRaw,
          node
        ).has(value)
      )
    );

    if (!match) return;

    this.available = true;

    this.label = window.FAFSizes.format(
      match.dataset.savedSizeRaw,
      {
        gender:
          match.dataset.sizeGender || '',
        category:
          match.dataset.sizeCategory || '',
        preference:
          this.$store.sizeSystem?.preference ||
          window.FAFSizes.preference ||
          saved.preferredSizeSystem ||
          saved.sizeSystem ||
          'UK',
      }
    );
  },
}));

    Alpine.data('contextualMerch', (variantId) => ({
      loading: false, added: false, error: '',
      add() {
        if (this.loading || !variantId || !Alpine.store('cart')?.addVariant) return;
        this.loading = true; this.error = '';
        Alpine.store('cart').addVariant(Number(variantId), (error) => {
          this.loading = false;
          if (error) return void (this.error = error.message || 'This item could not be added.');
          this.added = true; setTimeout(() => { this.added = false; }, 1800);
        }, null, null, null, {source: 'cart_contextual_merch'});
      }
    }));

    Alpine.data('emptyCartRecovery', (config = {}) => ({
      eligible: false, loading: false, resolved: false, visible: false, handles: [],
      key: String(config.key || 'recently-viewed-6'), maxShow: Number(config.maxShow || 3), sectionId: String(config.sectionId || 'empty-cart-history-products'), searchUrl: String(config.searchUrl || '/search'),
      init() { try { this.handles = [...new Set((JSON.parse(localStorage.getItem(this.key) || '[]') || []).map((v) => String(v || '').toLowerCase()).filter((v) => /^[a-z0-9-]+$/.test(v)))].slice(0,12); } catch (_) { this.handles = []; } this.eligible = this.handles.length > 0; if (!this.eligible) this.resolved = true; },
      async load() {
        if (!this.eligible || this.loading || this.resolved) return;
        this.loading = true;
        try {
          const params = new URLSearchParams({section_id: this.sectionId, q: this.handles.map((h) => `handle:${h}`).join(' OR '), type: 'product', 'options[unavailable_products]': 'hide', 'options[prefix]': 'none'});
          const response = await fetch(`${this.searchUrl}?${params}`, {credentials: 'same-origin', headers: {'X-Requested-With': 'XMLHttpRequest'}});
          if (!response.ok) throw new Error();
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html'), cards = new Map();
          doc.querySelectorAll('#empty-cart-history-results [data-empty-cart-history-handle]').forEach((card) => cards.set(String(card.dataset.emptyCartHistoryHandle || '').toLowerCase(), card));
          const fragment = document.createDocumentFragment(); let shown = 0;
          this.handles.some((handle) => { const card = cards.get(handle); if (!card) return false; fragment.appendChild(card.cloneNode(true)); return ++shown >= this.maxShow; });
          if (shown) { this.$refs.items.replaceChildren(fragment); this.visible = true; }
        } catch (_) { this.visible = false; } finally { this.loading = false; this.resolved = true; }
      },
      handleCardClick(event) {
        const button = event.target.closest('[data-empty-cart-add]'); if (!button || button.disabled) return;
        event.preventDefault(); const id = Number(button.dataset.emptyCartAdd || 0); if (!id || !Alpine.store('cart')?.addVariant) return;
        button.disabled = true;
        Alpine.store('cart').addVariant(id, () => { button.disabled = false; }, null, null, null, {source: 'empty_cart_recovery'});
      }
    }));
  }

  if (window.Alpine) register();
  document.addEventListener('alpine:init', register, {once: true});
})();
